"use client";

/**
 * ReflectRhythmsGrid — 2-column "Drift / Steady" grid below the AI card.
 *
 * Drift cards come from `goalDriftSummary.top_drifting_goals` (goals with
 * stale activity / open steps). Steady cards come from active goals that
 * are NOT drifting AND have non-trivial progress, OR from
 * `projection.pulse.steady` when the goal list is empty.
 *
 * On row click we navigate to the goal detail (delegated to host).
 */

import type { GoalDriftGoal, GoalItem } from "@/lib/api";
import type { ReflectPulse } from "@/lib/api/projection-types";
import styles from "./reflect-page.module.css";

export interface ReflectRhythmsGridProps {
  driftGoals: GoalDriftGoal[];
  steadyGoals: GoalItem[];
  pulse?: ReflectPulse | null;
  /** Optional: fired when a card is tapped. Title is supplied alongside
   *  the id so the host can prime the goal-focus store before navigating. */
  onGoalClick?: (goalId: string, goalTitle: string) => void;
  /** Tunes how many cards each side shows. Default 3. */
  maxPerColumn?: number;
}

interface RhythmCard {
  id: string;
  tag: string;
  title: string;
  note: string;
  kind: "drift" | "steady";
}

function buildDriftCards(goals: GoalDriftGoal[], max: number): RhythmCard[] {
  return goals.slice(0, max).map((g, i) => {
    // Backend `days_since_activity` can come back negative when the goal
    // was just created or the activity log is in the future — clamp to 0
    // so we never show "-1d without progress" to the user.
    const days = Math.max(0, g.days_since_activity ?? 14);
    const openSteps = Math.max(0, g.open_task_count ?? 0);
    const stepWord = openSteps === 1 ? "step" : "steps";
    const note =
      g.recovery_note ??
      (days === 0
        ? `${openSteps} open ${stepWord}; no completed progress yet.`
        : `${openSteps} open ${stepWord}, no completed progress in the last ${days} days.`);
    return {
      id: g.id,
      tag: i === 0 ? "Recovery target" : "Still at risk",
      title: g.title,
      note,
      kind: "drift" as const,
    };
  });
}

function buildSteadyCards(
  goals: GoalItem[],
  driftIds: Set<string>,
  pulse: ReflectPulse | null | undefined,
  max: number,
): RhythmCard[] {
  const candidates = goals
    .filter(
      (g) =>
        !driftIds.has(g.id) &&
        g.status === "active" &&
        (g.progress_pct ?? 0) > 0,
    )
    .sort((a, b) => (b.progress_pct ?? 0) - (a.progress_pct ?? 0));

  if (candidates.length === 0 && pulse?.steady?.length) {
    // Pulse signals don't carry a goal id; render with a synthetic key.
    return pulse.steady.slice(0, max).map((s, i) => ({
      id: `pulse-${i}`,
      tag: "Keep in view",
      title: s.label,
      note: "This rhythm has been holding up over the last few weeks.",
      kind: "steady",
    }));
  }

  return candidates.slice(0, max).map((g) => ({
    id: g.id,
    tag: "Keep in view",
    title: g.title,
    note: "Path is active — holds better if next move stays specific.",
    kind: "steady" as const,
  }));
}

function CardList({
  items,
  onClick,
}: {
  items: RhythmCard[];
  onClick?: (id: string, title: string) => void;
}) {
  if (items.length === 0) {
    return (
      <p className={styles["rf-rhythm-empty"]}>Nothing to surface yet.</p>
    );
  }
  return (
    <>
      {items.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`${styles["rf-card"]} ${
            c.kind === "drift" ? styles["is-drift"] : styles["is-steady"]
          }`}
          onClick={onClick ? () => onClick(c.id, c.title) : undefined}
          data-testid={`reflect-rhythm-card-${c.id}`}
        >
          <span className={styles["rf-card-tag"]}>{c.tag}</span>
          <span className={styles["rf-card-h"]}>{c.title}</span>
          <span className={styles["rf-card-meta"]}>{c.note}</span>
        </button>
      ))}
    </>
  );
}

export default function ReflectRhythmsGrid({
  driftGoals,
  steadyGoals,
  pulse,
  onGoalClick,
  maxPerColumn = 3,
}: ReflectRhythmsGridProps) {
  const driftCards = buildDriftCards(driftGoals, maxPerColumn);
  const driftIds = new Set(driftGoals.map((g) => g.id));
  const steadyCards = buildSteadyCards(
    steadyGoals,
    driftIds,
    pulse ?? null,
    maxPerColumn,
  );

  return (
    <div className={styles["rf-rhythms"]}>
      <div className={styles["rf-rhythm-col"]}>
        <h3 className={`${styles["rf-rhythm-h"]} ${styles["is-drift"]}`}>
          <span className={styles["rf-rhythm-dot"]} aria-hidden /> Drift ·{" "}
          {driftCards.length}
        </h3>
        <CardList items={driftCards} onClick={onGoalClick} />
      </div>
      <div className={styles["rf-rhythm-col"]}>
        <h3 className={`${styles["rf-rhythm-h"]} ${styles["is-steady"]}`}>
          <span className={styles["rf-rhythm-dot"]} aria-hidden /> Steady ·{" "}
          {steadyCards.length}
        </h3>
        <CardList items={steadyCards} onClick={onGoalClick} />
      </div>
    </div>
  );
}
